<template>
  <section class="level-result" aria-live="polite">
    <p class="result-eyebrow">{{ $t(`levelTest.${skill}Result`) }}</p>
    <p class="result-level">
      <span class="result-code">{{ level }}</span>
    </p>
    <p class="result-description">{{ $t(`learnerProfile.${skill}Levels.${level}`) }}</p>
    <p v-if="saved" class="result-saved">
      <span class="material-icons" aria-hidden="true">check_circle</span>{{ $t('levelTest.savedToProfile') }}
    </p>

    <div v-if="$slots.default" class="result-details">
      <slot />
    </div>

    <div class="result-actions">
      <button type="button" class="primary-button" @click="navigateToView(continuingSetup ? 'Profile' : 'Material')">
        {{ $t(continuingSetup ? 'learnerProfile.continueSetup' : 'levelTest.browseMaterials') }}
      </button>
      <button v-if="canRetake" type="button" class="secondary-button" @click="$emit('retake')">
        {{ $t('levelTest.retake') }}
      </button>
    </div>
  </section>
</template>

<script>
import { useViews } from '@/composables/views'
import { useLearnerProfile } from '@/composables/useLearnerProfile'

export default {
  name: 'LevelResult',
  props: {
    skill: { type: String, required: true },
    level: { type: String, required: true },
    canRetake: { type: Boolean, default: true },
    saved: { type: Boolean, default: true }
  },
  emits: ['retake'],
  setup() {
    const { handleViewChange } = useViews()
    const { onboardingSession } = useLearnerProfile()
    return { navigateToView: handleViewChange, onboardingSession }
  },
  computed: {
    continuingSetup() {
      return this.$route.query.setup === '1' && Boolean(this.onboardingSession)
    }
  }
}
</script>

<style scoped>
.level-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-10) var(--space-6);
  text-align: center;
}

.result-eyebrow {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.result-level {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  margin: var(--space-3) 0 0;
}

.result-code {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-3xl);
  font-weight: 600;
}

.result-description {
  max-width: 28rem;
  margin: var(--space-4) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  line-height: 1.55;
}

.result-saved {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: var(--space-3) 0 0;
  color: var(--signal-active-strong);
  font-size: var(--font-size-sm);
}

.result-saved .material-icons {
  font-size: 16px;
}

.result-details {
  width: 100%;
  max-width: 28rem;
  margin-top: var(--space-8);
}

.result-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-8);
}

.primary-button,
.secondary-button {
  min-height: 44px;
  padding: 0 var(--space-6);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.primary-button {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.secondary-button {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.primary-button:focus-visible,
.secondary-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (max-width: 640px) {
  .result-actions {
    flex-direction: column;
    align-self: stretch;
  }
}
</style>
